/**
 * Feature E06-F02 — Research Run / Progress screen.
 *
 * The screen the audit flagged as MISSING (docs/fidelity-audit-2026-07.md, Research
 * TIER 1). 1:1 port of the prototype run screen
 * `../EER app/prototype/assets/research.proto.js` (renderRun / renderRunEdge /
 * runStepper, L624-779):
 *   - a 5-stage rail: Queued → Discovering → Verifying About-Us →
 *     Scoring & ranking → Ready for review;
 *   - a status pill (spinner while working, green check when ready);
 *   - a progress bar + counters (scored / total · discovered · errors);
 *   - a live partial-results table (top matches appear as they are scored);
 *   - Cancel + Retry-errors actions and a "Review long-list" gate button
 *     (enabled only once the run is ready);
 *   - an "AI check" button (E06-F05-T05) — the in-app entry to the manual, paid
 *     per-company Haiku chain; pure navigation, spends nothing by itself;
 *   - the two edge states: `failed` (crash → resume/retry, partial saved) and
 *     `empty`/low-yield (rare geo → suggested relaxations);
 *   - the REQUEST CRITERIA recap (E06-F02-T10) — the criteria the search was
 *     started with, rendered in every run body. Without it, re-entering Run for an
 *     already-finished request (`in_review`, reached via Review → back) showed an
 *     empty shell: no title (the host prop is unset on that path), no criteria, an
 *     empty partial table. The criteria come from `research_status.request`.
 *
 * Data source — REAL, not mocked. The screen polls the backend orchestrator
 * snapshot `GET /research/:id/status` (researchOrchestratorApi → useResearchStatus)
 * every few seconds until the run is terminal/paused; the counters, rail stage and
 * partial rows all come from that snapshot. There is NO fabricated progress — a
 * request with no job yet renders the queued state and waits for the first tick.
 * (The backend also exposes an SSE stream carrying the identical payload; polling
 * is used here for simplicity — see researchOrchestratorApi.ts.)
 *
 * No `dangerouslySetInnerHTML` — every server value (title, company name, counts)
 * is React-escaped.
 */
import { useMemo, type JSX } from 'react';
import type { PartialRow, RunRequestSummary } from '@eer/shared';
import { gradeChipStyle } from './researchReviewModel.js';
import {
  RUN_RAIL_STAGES,
  RUN_RAIL_LABELS,
  runPresentationState,
  railStepStatus,
  runStatusLabel,
  processStepForRun,
  scoringAwaitsAiCheck,
  progressPercent,
  isLowYield,
  SUGGESTED_RELAXATIONS,
  type RunPresentationState,
} from './researchRunModel.js';
import {
  useResearchStatus,
  useCancelResearchRun,
  useRetryResearchRun,
} from './researchOrchestratorApi.js';
import { useToast } from '../ui/ToastContext.js';
import { DiscoveryIssuesBanner } from './DiscoveryIssuesBanner.js';
import { DiscoveryTilesTable } from './DiscoveryTilesTable.js';
// E06-F02-T10 recap — shared with the Review screen since E07-F04-T16.
import { RequestCriteriaPanel } from './RequestCriteriaPanel.js';
// E06-F01-T14 — recruiter + team leads of the linked project.
import { ResearchProjectPeople } from './ResearchProjectPeople.js';
// E06-F05-T14 — discovery → AI check → scoring → review explanation.
import { ResearchProcessSteps } from './ResearchProcessSteps.js';

// ---------------------------------------------------------------------------
// public props
// ---------------------------------------------------------------------------

export interface ResearchRunViewProps {
  /** The research request whose run is displayed / driven. */
  readonly requestId: number;
  /** Request title for the run header (falls back to the id when unknown). */
  readonly title?: string;
  /** Back to the Intake form ("Edit criteria" / "Broaden criteria"). */
  readonly onEditCriteria?: () => void;
  /** Open the Review long-list (enabled once the run is ready / gated). */
  readonly onReview?: () => void;
  /**
   * Open the manual AI-check screen (E06-F05-T05). Discovery itself is free and
   * stops at `deduped`; the paid per-company AI chain is started by hand there, so
   * this is a plain navigation affordance — it never starts anything paid.
   */
  readonly onAiCheck?: () => void;
  /** Return to the library/history (after a Cancel). */
  readonly onBackToLibrary?: () => void;
}

// ---------------------------------------------------------------------------
// stage rail (prototype stepsRow L647-657)
// ---------------------------------------------------------------------------

function StageRail({
  state,
  scored,
}: {
  state: RunPresentationState;
  scored: number;
}): JSX.Element {
  return (
    <div className="flex items-start mb-8" data-testid="run-stage-rail">
      {RUN_RAIL_STAGES.map((stage, i) => {
        // E06-F05-T14 — "Scoring & ranking" is never shown done while 0 are scored.
        const status = railStepStatus(stage, state, scored);
        const isActive = status === 'active';
        const done = status === 'done';
        const awaiting = status === 'awaiting';
        const dot = done
          ? 'bg-green-500 text-white'
          : isActive
            ? 'bg-[#D4AF37] text-slate-900'
            : awaiting
              ? 'bg-white text-amber-600 ring-2 ring-amber-400'
              : 'bg-slate-200 text-slate-400';
        const last = i === RUN_RAIL_STAGES.length - 1;
        return (
          <div
            key={stage}
            className={`flex items-center ${last ? '' : 'flex-1'}`}
            data-testid={`run-rail-step-${stage}`}
            data-active={isActive ? 'true' : 'false'}
            data-done={done ? 'true' : 'false'}
            data-step-status={status}
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full ${dot} flex items-center justify-center text-xs`}
              >
                {done ? <i className="fa-solid fa-check" aria-hidden /> : i + 1}
              </div>
              <span
                className={`text-[10px] mt-1 whitespace-nowrap ${
                  isActive ? 'font-bold text-slate-800' : 'text-slate-400'
                }`}
              >
                {RUN_RAIL_LABELS[stage]}
              </span>
              {awaiting ? (
                <span
                  className="text-[10px] text-amber-600 whitespace-nowrap"
                  data-testid="run-rail-scoring-awaiting"
                >
                  after AI check
                </span>
              ) : null}
            </div>
            {last ? null : (
              <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-green-500' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// live partial-results table (prototype preview L636-644)
// ---------------------------------------------------------------------------

function PartialRowLine({ row, rank }: { row: PartialRow; rank: number }): JSX.Element {
  const scored = row.score != null;
  const style = gradeChipStyle(row.grade);
  const name = row.name ?? 'Unnamed company';
  return (
    <tr className="border-b border-gray-50" data-testid="run-partial-row">
      <td className="px-3 py-2 text-xs text-gray-400 w-8">{rank}</td>
      <td className="px-3 py-2 text-sm font-medium text-slate-700">
        {name}
        {row.needsHuman ? (
          <span
            className="ml-1 text-amber-500"
            title="Low confidence — needs human review"
            data-testid="run-partial-needs-human"
          >
            <i className="fa-solid fa-triangle-exclamation text-[10px]" aria-hidden />
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2">
        {scored ? (
          <span
            className={`inline-flex items-center justify-center ${
              style.isPlaceholder
                ? 'px-1.5 h-6 text-[10px] font-medium'
                : 'w-6 h-6 text-xs font-bold'
            } rounded-full ring-1 ${style.className}`}
          >
            {style.label}
          </span>
        ) : (
          <span className="text-xs text-slate-400">
            <i className="fa-solid fa-circle-notch fa-spin mr-1" aria-hidden />
            scoring…
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {scored ? (
          <span className="text-sm font-bold text-slate-800">{row.score}</span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// run header (navy bar — prototype L666-674)
// ---------------------------------------------------------------------------

function RunHeader({
  title,
  requestId,
  subtitle,
  pill,
}: {
  title: string;
  requestId: number;
  subtitle: string;
  pill: JSX.Element;
}): JSX.Element {
  return (
    <div className="bg-slate-900 p-6 text-white flex justify-between items-start">
      <div>
        <h1 className="brand-font text-2xl text-[#D4AF37]" data-testid="run-title">
          {title}
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          #{requestId} · {subtitle}
        </p>
      </div>
      {pill}
    </div>
  );
}

/**
 * Navigation affordance to the manual AI-check screen (E06-F05-T05). Rendered only
 * when the host wired `onAiCheck` — it is the ONLY in-app path from the Run screen
 * to the paid per-company chain, so it must never be dropped from the JSX again
 * (the prop existed but was never rendered, leaving the screen unreachable).
 * Clicking it spends nothing: the paid run needs an explicit click on the next
 * screen, behind a confirmation.
 */
function AiCheckButton({ onAiCheck }: { onAiCheck?: () => void }): JSX.Element | null {
  if (!onAiCheck) return null;
  return (
    <button
      type="button"
      data-testid="run-ai-check"
      onClick={onAiCheck}
      title="Select companies and run the paid AI check by hand — nothing runs automatically"
      className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded text-sm font-semibold hover:bg-slate-50"
    >
      <i className="fa-solid fa-robot mr-1" aria-hidden /> AI check
    </button>
  );
}

/**
 * Header run status (E06-F02-T13). Deliberately NOT button-shaped: the old filled
 * rounded pill ("Ready for review" on solid green) read as a clickable button that
 * led nowhere. It is now a plain status line — "STATUS" caption + coloured icon and
 * text, no fill/border, default cursor. The only way into Review is the explicit
 * `run-review-btn` ("Review long-list →") in the actions row.
 */
function HeaderStatus({
  tone,
  icon,
  label,
  testId,
  ready,
}: {
  tone: 'green' | 'gold' | 'red' | 'amber';
  icon: string;
  label: string;
  testId?: string;
  ready?: boolean;
}): JSX.Element {
  const toneClass = {
    green: 'text-green-400',
    gold: 'text-[#D4AF37]',
    red: 'text-red-400',
    amber: 'text-amber-400',
  }[tone];
  return (
    <div role="status" className="text-right cursor-default select-none">
      <div className="text-[10px] uppercase tracking-wider text-slate-500" aria-hidden>
        Status
      </div>
      {/* testid sits on the label line so its text stays exactly the status label */}
      <div
        data-testid={testId}
        data-ready={ready === undefined ? undefined : ready ? 'true' : 'false'}
        className={`text-sm font-semibold ${toneClass}`}
      >
        <i className={`fa-solid mr-1.5 ${icon}`} aria-hidden />
        {label}
      </div>
    </div>
  );
}

function StatusPill({ ready, label }: { ready: boolean; label: string }): JSX.Element {
  return (
    <HeaderStatus
      testId="run-status-pill"
      ready={ready}
      tone={ready ? 'green' : 'gold'}
      icon={ready ? 'fa-circle-check' : 'fa-circle-notch fa-spin'}
      label={label}
    />
  );
}

// ---------------------------------------------------------------------------
// root view
// ---------------------------------------------------------------------------

export function ResearchRunView({
  requestId,
  title,
  onEditCriteria,
  onReview,
  onAiCheck,
  onBackToLibrary,
}: ResearchRunViewProps): JSX.Element {
  const toast = useToast();
  const { data, isLoading } = useResearchStatus(requestId);
  const cancel = useCancelResearchRun(requestId);
  const retry = useRetryResearchRun(requestId);

  const job = data?.job;
  const partial = useMemo<readonly PartialRow[]>(() => data?.topPartial ?? [], [data?.topPartial]);
  // E06-F02-T10 — the request context rides on the status snapshot. It is the ONLY
  // source of the criteria (and the reliable source of the title: the host-carried
  // `title` prop is empty whenever the screen is reached by open-by-status, e.g.
  // Review → back, which is exactly the reported blank-screen path).
  const request: RunRequestSummary | null = data?.request ?? null;
  const criteria = request?.criteria ?? null;

  const state: RunPresentationState = job
    ? runPresentationState(job.status, job.currentStage)
    : 'queued';
  const ready = state === 'ready';
  const total = job?.total ?? 0;
  const completed = job?.completed ?? 0;
  const errorCount = job?.errorCount ?? 0;
  const discovered = partial.length > total ? partial.length : total;
  const pct = progressPercent(completed, total, ready);
  const lowYield = isLowYield(state, total);
  // E06-F05-T14 — finished discovery with nothing scored: the AI check is next.
  const awaitingAiCheck = scoringAwaitsAiCheck(state, completed);
  const serverTitle = request?.title?.trim() ?? '';
  const displayTitle =
    title && title.trim().length > 0
      ? title
      : serverTitle.length > 0
        ? serverTitle
        : `Research request #${requestId}`;

  function handleCancel(): void {
    cancel.mutate(undefined, {
      onSuccess: () => {
        toast.success('Run cancelled — partial results are saved.');
        onBackToLibrary?.();
      },
      onError: () => toast.error('Failed to cancel the run'),
    });
  }

  function handleRetry(): void {
    retry.mutate(undefined, {
      onSuccess: () => toast.success('Resuming the run — retrying unfinished companies…'),
      onError: () => toast.error('Failed to retry the run'),
    });
  }

  const backButton = (
    <button
      type="button"
      data-testid="run-edit-criteria"
      onClick={onEditCriteria}
      className="mb-4 text-sm text-slate-500 hover:text-slate-800 flex items-center"
    >
      <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> Edit criteria
    </button>
  );

  // -------------------------------------------------------------------------
  // edge state: FAILED (crash → resume/retry; partial saved)
  // -------------------------------------------------------------------------
  if (state === 'failed') {
    const remaining = Math.max(0, total - completed);
    return (
      <div data-testid="research-run-view" data-run-state="failed">
        {backButton}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-w-5xl">
          <RunHeader
            title={displayTitle}
            requestId={requestId}
            subtitle="job interrupted"
            pill={<HeaderStatus tone="red" icon="fa-triangle-exclamation" label="Failed" />}
          />
          <div className="p-8">
            {/* The criteria stay visible on the crash edge too — a resume decision
                is made against them. */}
            <ResearchProjectPeople projectId={request?.projectId} testIdPrefix="run" />
            <RequestCriteriaPanel criteria={criteria} />
            <div
              data-testid="run-failed-banner"
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-800"
            >
              <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden />{' '}
              <b>
                Run failed at {completed}/{total}.
              </b>{' '}
              {job?.lastError ?? 'The enrichment provider timed out.'} Partial results are saved —
              resume picks up where it stopped; no work is lost.
            </div>
            {/* E07-F02-T11 — every discovery search failed: the per-search reasons. */}
            <DiscoveryIssuesBanner
              issues={job?.discoveryIssues ?? []}
              variant="error"
              testId="run-discovery-issues"
            />
            {/* E07-F02-T17 — the same searches as a table: which tile failed and why. */}
            <DiscoveryTilesTable tiles={job?.discoveryTiles ?? []} testId="run-discovery-tiles" />
            <div className="grid grid-cols-3 gap-4 mb-6 text-center text-sm">
              <div className="p-3 bg-slate-50 rounded">
                <div className="text-2xl font-bold text-slate-900">{completed}</div>
                <div className="text-[10px] uppercase text-gray-400">Scored (saved)</div>
              </div>
              <div className="p-3 bg-slate-50 rounded">
                <div className="text-2xl font-bold text-red-600">{remaining}</div>
                <div className="text-[10px] uppercase text-gray-400">Remaining</div>
              </div>
              <div className="p-3 bg-slate-50 rounded">
                <div className="text-2xl font-bold text-amber-600">{errorCount}</div>
                <div className="text-[10px] uppercase text-gray-400">Errored</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="run-resume"
                disabled={retry.isPending}
                onClick={handleRetry}
                className="px-5 py-2.5 bg-[#D4AF37] text-slate-900 rounded font-bold text-sm hover:bg-[#B5952F] shadow disabled:opacity-60"
              >
                <i className="fa-solid fa-rotate-right mr-1" aria-hidden />
                {retry.isPending ? 'Resuming…' : 'Resume run'}
              </button>
              {completed > 0 ? (
                <button
                  type="button"
                  data-testid="run-review-partial"
                  onClick={onReview}
                  className="px-4 py-2.5 border border-slate-200 text-slate-600 rounded text-sm hover:bg-slate-50"
                >
                  Review partial ({completed})
                </button>
              ) : null}
              {/* The discovery job crashed, but the companies it did save can still
                  be checked by hand — the AI chain is per-company and independent
                  of the job's own state. */}
              <AiCheckButton onAiCheck={onAiCheck} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // edge state: EMPTY / LOW-YIELD (rare geo → suggested relaxations)
  // -------------------------------------------------------------------------
  if (lowYield) {
    return (
      <div data-testid="research-run-view" data-run-state="empty">
        {backButton}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-w-5xl">
          <RunHeader
            title={displayTitle}
            requestId={requestId}
            subtitle="rare geo / narrow niche"
            pill={<HeaderStatus tone="amber" icon="fa-circle-info" label="Low yield" />}
          />
          <div className="p-8">
            {/* "Broaden criteria" is meaningless without seeing the current ones. */}
            <ResearchProjectPeople projectId={request?.projectId} testIdPrefix="run" />
            <RequestCriteriaPanel criteria={criteria} />
            <div
              data-testid="run-lowyield-banner"
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800"
            >
              <i className="fa-solid fa-magnifying-glass mr-1" aria-hidden />{' '}
              <b>Only {total} companies found.</b> The pool for this industry × geo is exhausted.
              Broaden criteria to grow the long-list.
            </div>
            {/* E07-F02-T17 — which tiles found little, and why. */}
            <DiscoveryTilesTable tiles={job?.discoveryTiles ?? []} testId="run-discovery-tiles" />
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-xs font-bold uppercase text-slate-500 mb-2">
                Suggested relaxations
              </p>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="run-suggested-relaxations">
                {SUGGESTED_RELAXATIONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={onEditCriteria}
                    className="px-3 py-1 rounded-full border border-gray-200 hover:border-slate-300 text-slate-600"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="run-broaden-criteria"
                onClick={onEditCriteria}
                className="px-5 py-2.5 bg-[#D4AF37] text-slate-900 rounded font-bold text-sm hover:bg-[#B5952F] shadow"
              >
                <i className="fa-solid fa-sliders mr-1" aria-hidden /> Broaden criteria
              </button>
              <button
                type="button"
                data-testid="run-review-anyway"
                onClick={onReview}
                className="px-4 py-2.5 border border-slate-200 text-slate-600 rounded text-sm hover:bg-slate-50"
              >
                Review {total} anyway
              </button>
              {/* A thin long-list is exactly the case where the recruiter wants to
                  AI-check the few companies that WERE found — without this the
                  low-yield screen would be a dead end to the check flow. */}
              <AiCheckButton onAiCheck={onAiCheck} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // live progress screen (queued / discovering / verifying / scoring / ready)
  // -------------------------------------------------------------------------
  return (
    <div data-testid="research-run-view" data-run-state={state}>
      {backButton}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-w-5xl">
        <RunHeader
          title={displayTitle}
          requestId={requestId}
          subtitle={ready ? 'run complete' : 'run in progress'}
          pill={<StatusPill ready={ready} label={runStatusLabel(state, completed)} />}
        />

        <div className="p-8">
          {/* stage rail */}
          <StageRail state={state} scored={completed} />

          {/* E06-F05-T14 — how the process goes and where this run is in it */}
          <ResearchProcessSteps
            current={processStepForRun(state, completed)}
            testId="run-process-steps"
          />

          {/* the criteria this run was started with (E06-F02-T10) */}
          {/* E06-F01-T14 — the linked project's recruiter + team leads */}
          <ResearchProjectPeople projectId={request?.projectId} testIdPrefix="run" />
          <RequestCriteriaPanel criteria={criteria} />

          {/* progress bar + counters */}
          <div className="mb-2 flex justify-between text-xs text-slate-500">
            <span>
              <b className="text-slate-800" data-testid="run-scored-count">
                {completed}
              </b>{' '}
              of <span data-testid="run-total-count">{total}</span> companies scored ·{' '}
              <span data-testid="run-discovered-count">{discovered}</span> discovered ·{' '}
              <span className="text-red-500" data-testid="run-error-count">
                {errorCount} errors{errorCount > 0 ? ' (retrying)' : ''}
              </span>
            </span>
            <span className="font-bold text-slate-800" data-testid="run-percent">
              {pct}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden mb-6">
            <div
              data-testid="run-progress-bar"
              className="h-full bg-slate-900 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>

          {/* E07-F02-T17 — per-tile search results (found / duplicates / new / why zero). */}
          <DiscoveryTilesTable tiles={job?.discoveryTiles ?? []} testId="run-discovery-tiles" />

          {/* live partial results */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                <i className="fa-solid fa-satellite-dish text-[#D4AF37] mr-1" aria-hidden /> Live
                results — top matches appear as found
              </span>
              <span className="text-[10px] text-gray-400">partial · auto-refreshing</span>
            </div>
            <table className="w-full">
              <tbody data-testid="run-partial-table">
                {partial.length > 0 ? (
                  partial.map((row, i) => <PartialRowLine key={row.id} row={row} rank={i + 1} />)
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-8 text-center text-xs text-gray-400"
                      data-testid="run-partial-empty"
                    >
                      {isLoading
                        ? 'Connecting to the run…'
                        : awaitingAiCheck
                          ? 'No companies scored yet — scoring happens only after the AI check. Open AI check to pick companies.'
                          : 'No scored companies yet — results appear as the run progresses.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* actions */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="run-cancel"
                disabled={cancel.isPending || ready}
                onClick={handleCancel}
                className="px-4 py-2 border border-red-200 text-red-600 rounded text-sm hover:bg-red-50 disabled:opacity-50"
              >
                <i className="fa-solid fa-stop mr-1" aria-hidden />
                {cancel.isPending ? 'Cancelling…' : 'Cancel'}
              </button>
              <button
                type="button"
                data-testid="run-retry-errors"
                disabled={retry.isPending || errorCount === 0}
                onClick={handleRetry}
                className="px-4 py-2 border border-slate-200 text-slate-600 rounded text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <i className="fa-solid fa-rotate-right mr-1" aria-hidden />
                {retry.isPending ? 'Retrying…' : 'Retry errors'}
              </button>
            </div>
            <div className="flex gap-2">
              {/* E06-F05-T05 entry point. Discovery is free and stops at `deduped`;
                  the PAID per-company chain is started by hand on the AI-check
                  screen, so this button only navigates — it never spends money and
                  is therefore not gated on `ready` (a partially-scored list can be
                  checked as soon as companies exist). */}
              <AiCheckButton onAiCheck={onAiCheck} />
              <button
                type="button"
                data-testid="run-review-btn"
                disabled={!ready}
                onClick={onReview}
                className={`px-5 py-2.5 rounded font-bold text-sm transition ${
                  ready
                    ? 'bg-[#D4AF37] text-slate-900 hover:bg-[#B5952F] shadow cursor-pointer'
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                Review long-list <i className="fa-solid fa-arrow-right ml-1" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResearchRunView;
